/**
 * Offline/sync status store
 *
 * Manages:
 * - Online/offline status
 * - Offline queue state
 * - Sync status and errors
 * - Queue persistence
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface QueuedMutation {
  id: string
  type: string
  timestamp: number
  payload: Record<string, unknown>
  retry: number
  lastError?: string
}

export const useOfflineStore = defineStore('offline', () => {
  const isOnline = ref(navigator.onLine)
  const queue = ref<QueuedMutation[]>([])
  const isSyncing = ref(false)
  const syncError = ref<string | null>(null)

  const hasQueue = computed(() => queue.value.length > 0)
  const queueLength = computed(() => queue.value.length)

  const addToQueue = (mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retry'>) => {
    const queueItem: QueuedMutation = {
      ...mutation,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retry: 0,
    }
    queue.value.push(queueItem)
    persistQueue()
  }

  const removeFromQueue = (id: string) => {
    queue.value = queue.value.filter((item) => item.id !== id)
    persistQueue()
  }

  const clearQueue = () => {
    queue.value = []
    persistQueue()
  }

  const setOnline = (online: boolean) => {
    isOnline.value = online
    if (online) {
      syncError.value = null
    }
  }

  const setSyncing = (syncing: boolean) => {
    isSyncing.value = syncing
  }

  const setSyncError = (error: string | null) => {
    syncError.value = error
  }

  const persistQueue = () => {
    localStorage.setItem('offlineQueue', JSON.stringify(queue.value))
  }

  const loadQueue = () => {
    const stored = localStorage.getItem('offlineQueue')
    if (stored) {
      queue.value = JSON.parse(stored)
    }
  }

  // Initialize from storage
  loadQueue()

  // Listen to online/offline events
  window.addEventListener('online', () => setOnline(true))
  window.addEventListener('offline', () => setOnline(false))

  return {
    isOnline,
    queue,
    isSyncing,
    syncError,
    hasQueue,
    queueLength,
    addToQueue,
    removeFromQueue,
    clearQueue,
    setOnline,
    setSyncing,
    setSyncError,
    loadQueue,
  }
})
